/**
 * Material Transfer V2 (MTR-03) — Weaving WIP → Finishing / Dyeing WIP on the canonical V2 stock engine.
 * Legacy metres-only log stays visible read-only. Stage Transfer is untouched.
 * MTR-03C · create/reverse controls follow the reactive RBAC context (no refresh after late permission load).
 * MTR-05A · Reverse availability comes from the server (can_reverse / reverse_block_reason); no client-side rules.
 * MTR-05C · visible View action + read-only detail panel (detail API legs); WO/CWO numbers from server lookups only.
 * MTR-05E · Actions column pinned right; icons grouped; block reason clamped to 2 lines with full-text tooltip.
 * MTR-05E-1 · register cells px-3; compact "Not reversible" (full reason in tooltip/aria/detail); detail timestamps in IST.
 * WOREF-04 · synthetic (source_type UAT) transfers hidden by default; server-enforced admin-only "Show test records";
 *            TEST / Non-standard no. / lineage-anomaly tags from server fields (no client inference, no number changes).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useRbac } from "@/lib/rbac";
import { fmtDateTimeSecIST } from "@/lib/datetime";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowRightIcon, TruckIcon, WarningIcon, ArrowCounterClockwiseIcon, EyeIcon, XIcon } from "@phosphor-icons/react";

const MOD = "production_material_transfer";
const inp = "w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const btn = "h-10 px-4 rounded-sm text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed";
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `mt-${Date.now()}-${Math.random()}`);
const errText = (e) => { const d = e?.response?.data?.detail; return (d && (d.message || d.code)) || d || "Request failed"; };
export const istTs = (v) => (v ? `${fmtDateTimeSecIST(v)} IST` : null);
const STICKY = "sticky right-0 z-10 bg-[var(--bg-1)] shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.6)]";
const STATUS_CLS = { Active: "bg-emerald-500/20 text-emerald-300", Reversed: "bg-zinc-500/20 text-zinc-300", "Needs Review": "bg-amber-500/20 text-amber-300" };

function StatusBadge({ s, id }) {
  return <span data-testid={`mt-status-${id}`} className={`text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase ${STATUS_CLS[s] || ""}`}>{s}</span>;
}

const REF_NOTE = {
  not_recorded: "Not recorded", wo_record_missing: "WO record not found", wo_number_missing: "WO number unavailable",
  cwo_record_missing: "CWO record not found", cwo_number_missing: "CWO number unavailable", parent_mismatch: "Parent WO mismatch",
};

const TAG = "inline-block mt-0.5 mr-1 px-1.5 py-px rounded-sm text-[10px] font-semibold tracking-wider uppercase";
function RefTags({ r }) {
  const id = r.transfer_pair_id, anomaly = String(r.lineage_status || "").startsWith("anomaly:");
  return (<>
    {r.is_test && <span className={`${TAG} bg-fuchsia-500/15 text-fuchsia-300`} data-testid={`mt-v2-test-tag-${id}`} title="Synthetic UAT test record (linked WO/CWO source_type UAT)">TEST</span>}
    {!r.is_test && r.wo_number_standard === false && <span className={`${TAG} bg-amber-500/15 text-amber-300`} data-testid={`mt-v2-nonstd-${id}`} title="Document number does not follow WO-YY-YY-NNNNN(.N)">Non-standard no.</span>}
    {anomaly && <span className={`${TAG} bg-red-500/15 text-red-300`} data-testid={`mt-v2-lineage-${id}`} title={`Lineage integrity: ${r.lineage_status.slice(8)}`}>Lineage anomaly</span>}
  </>);
}

export function WoRef({ r }) {
  const id = r.transfer_pair_id;
  const note = r.wo_ref_status && r.wo_ref_status !== "ok" ? REF_NOTE[r.wo_ref_status] || "Unavailable" : null;
  const primary = r.cwo_id ? r.cwo_doc_no : r.wo_doc_no;
  return (
    <div className="whitespace-nowrap" data-testid={`mt-v2-wo-${id}`}>
      <div className="font-mono text-zinc-200">{primary || (r.cwo_id && r.wo_doc_no) || "—"}</div>
      {r.cwo_id && <div className="text-[11px] text-zinc-500" data-testid={`mt-v2-parent-wo-${id}`}>Parent: <span className="font-mono">{r.parent_wo_doc_no || "—"}</span></div>}
      {note && <div className="text-[11px] text-amber-300/80" data-testid={`mt-v2-wo-note-${id}`}>{note}</div>}
      <RefTags r={r} />
    </div>
  );
}

const Field = ({ label, children, id }) => (
  <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div><div className="text-zinc-200 break-words" data-testid={id}>{children ?? "—"}</div></div>
);

function DetailPanel({ d, legs, legsError, onClose, panelRef }) {
  return (
    <div ref={panelRef} className="tactical-card p-4 text-xs text-zinc-300 space-y-3" data-testid="mt-v2-detail">
      <div className="flex items-center justify-between">
        <div className="text-white font-semibold font-mono">{d.transfer_no} · {d.status}{d.historical ? " · historical (read-only)" : ""}</div>
        <button onClick={onClose} aria-label="Close transfer detail" title="Close" data-testid="mt-v2-detail-close" className="p-1.5 rounded-sm hover:bg-[var(--bg-2)] text-zinc-400"><XIcon size={14} /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Transfer date" id="mt-v2-detail-date">{String(d.transfer_date || d.date || "").slice(0, 10) || null}</Field>
        <Field label="From → To" id="mt-v2-detail-floors">{d.from_godown_code} → {d.to_godown_code}</Field>
        <Field label="Product" id="mt-v2-detail-product">{d.product_code}</Field>
        <Field label="Batch" id="mt-v2-detail-batch">{d.batch_lot}</Field>
        <Field label={`Quantity (${d.uom || "kg"})`} id="mt-v2-detail-qty">{d.qty}</Field>
        <Field label="WO / CWO" id="mt-v2-detail-wo"><WoRef r={d} /></Field>
        <Field label="Ledger WO/CWO ref" id="mt-v2-detail-ledger-ref">{d.ledger_wo_cwo_ref || "— (not recorded on ledger)"}</Field>
        <Field label="SO / ESO ref" id="mt-v2-detail-so">{d.so_eso_ref || null}</Field>
        <Field label="Route" id="mt-v2-detail-route">{d.route ? `Recorded ${d.route.recorded_stage} · selected ${d.route.selected_stage} · ${d.route.warning || "route matched"}${d.route.ack_required ? ` · acknowledged by ${d.route.acknowledged_by}` : ""}` : null}</Field>
        <Field label="Remarks" id="mt-v2-detail-remarks">{d.remarks || null}</Field>
        <Field label="Created by" id="mt-v2-detail-created-by">{d.created_by}</Field>
        <Field label="Created at (IST)" id="mt-v2-detail-created-at">{istTs(d.created_at)}</Field>
      </div>
      {d.reversal && <div data-testid="mt-v2-detail-reversal">Reversed by {d.reversal.by} at {istTs(d.reversal.at) || "—"}: {d.reversal.reason}</div>}
      {!d.can_reverse && d.reverse_block_reason && <div className="text-zinc-500" data-testid="mt-v2-detail-reverse-blocked">Reversal unavailable: {d.reverse_block_reason}</div>}
      <div className="text-zinc-500 font-mono">Pair {d.transfer_pair_id} · OUT {d.out_movement_id} · IN {d.in_movement_id}</div>
      {legsError ? <div className="text-red-300" data-testid="mt-v2-detail-legs-error">Ledger legs unavailable: {legsError}</div> : <LegsTable legs={legs} />}
    </div>
  );
}

function LegsTable({ legs }) {
  if (legs === null) return <div className="text-zinc-500" data-testid="mt-v2-detail-legs-loading">Loading ledger legs…</div>;
  return (
    <table className="w-full text-xs" data-testid="mt-v2-detail-legs">
      <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
        {["Movement", "Type", "Dir", "Floor", "Qty", "Posted (IST)", "WO/CWO ref"].map(h => <th key={h} className="text-left px-2 py-1.5 font-medium">{h}</th>)}
      </tr></thead>
      <tbody>{legs.map(l => (
        <tr key={l.movement_id} className="border-b border-[var(--border)]" data-testid={`mt-v2-leg-${l.movement_id}`}>
          <td className="px-2 py-1.5 font-mono text-zinc-400">{l.movement_id}</td><td className="px-2 py-1.5">{l.transaction_type}</td>
          <td className="px-2 py-1.5">{l.direction}</td><td className="px-2 py-1.5 font-mono">{l.warehouse_code || l.warehouse_id}</td>
          <td className="px-2 py-1.5 font-mono">{l.quantity}</td><td className="px-2 py-1.5 font-mono text-zinc-400 whitespace-nowrap" data-testid={`mt-v2-leg-posted-${l.movement_id}`}>{istTs(l.posted_at) || "—"}</td>
          <td className="px-2 py-1.5 font-mono text-zinc-400">{l.wo_cwo_ref || "—"}</td>
        </tr>))}
        {legs.length === 0 && <tr><td colSpan={7} className="px-2 py-2 text-zinc-500">No ledger legs returned</td></tr>}
      </tbody>
    </table>
  );
}

function NewTransferDialog({ open, onClose, onPosted }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(-1);
  const [stage, setStage] = useState("");
  const [qty, setQty] = useState("");
  const [ack, setAck] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(newKey());

  useEffect(() => {
    if (!open) return;
    keyRef.current = newKey(); setSel(-1); setStage(""); setQty(""); setAck(false); setRemarks("");
    api.get("/material-transfers/v2/eligible-sources").then(r => setRows(r.data?.rows || [])).catch(e => toast.error(errText(e)));
  }, [open]);

  const row = rows[sel];
  const dest = useMemo(() => row?.destinations?.find(d => d.stage === stage), [row, stage]);
  const q = parseFloat(qty);
  const canPost = row?.eligible && dest?.godown && !dest.blocked_reason && q > 0 && q <= row.available_qty && (!dest.ack_required || ack) && !busy;

  const post = async () => {
    setBusy(true);
    try {
      const r = await api.post("/material-transfers/v2", {
        idempotency_key: keyRef.current, from_godown_id: row.from_godown.id, to_godown_id: dest.godown.id,
        wo_id: row.wo_id, product_code: row.product_code, batch_lot: row.batch_lot, qty: q, uom: "kg",
        transfer_date: new Date().toISOString().slice(0, 10), remarks,
        route_ack: dest.ack_required ? { acknowledged: ack, ack_token: dest.ack_token } : { ack_token: dest.ack_token },
      });
      toast.success(`Transfer ${r.data.transfer_no} posted`);
      onPosted(r.data); onClose();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="mt-v2-dialog">
        <DialogHeader><DialogTitle>New Material Transfer</DialogTitle></DialogHeader>
        <label className="text-xs text-zinc-400">Weaving WIP stock (WO · product · batch)
          <select className={inp} value={sel} onChange={e => { setSel(+e.target.value); setStage(""); setAck(false); }} data-testid="mt-v2-source">
            <option value={-1}>— Select source batch —</option>
            {rows.map((r, i) => <option key={`${r.from_godown.id}-${r.batch_lot}-${r.product_code}`} value={i} disabled={!r.eligible}>
              {r.from_godown.code} · {r.wo_no || "?"} · {r.product_code} · {r.batch_lot} · {r.available_qty} kg{r.eligible ? "" : ` — Not Eligible: ${r.blocked_reason || "not eligible"}`}</option>)}
          </select>
        </label>
        {row?.eligible && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="text-xs text-zinc-400" data-testid="mt-v2-recorded-stage">WO recorded next stage:
              <div className="text-white font-mono text-sm mt-1">{row.routing?.recorded_stage} <span className="text-zinc-500">({row.routing?.product_routing || "no routing"})</span></div>
            </div>
            <div className="text-xs text-zinc-400" data-testid="mt-v2-available">Available:
              <div className="text-white font-mono text-sm mt-1">{row.available_qty} kg</div>
            </div>
            <label className="text-xs text-zinc-400">Destination
              <select className={inp} value={stage} onChange={e => { setStage(e.target.value); setAck(false); }} data-testid="mt-v2-destination">
                <option value="">— Select —</option>
                {row.destinations.map(d => <option key={d.stage} value={d.stage} disabled={!d.godown || !!d.blocked_reason}>
                  {d.stage === "FINISHING" ? "Finishing WIP" : "Dyeing WIP"} {d.godown ? `(${d.godown.code})` : "(unavailable)"}</option>)}
              </select>
            </label>
            <label className="text-xs text-zinc-400">Quantity (kg)
              <input type="number" min="0" step="0.0001" className={inp} value={qty} onChange={e => setQty(e.target.value)} data-testid="mt-v2-qty" />
            </label>
            <label className="text-xs text-zinc-400 col-span-2">Remarks
              <input className={inp} value={remarks} onChange={e => setRemarks(e.target.value)} data-testid="mt-v2-remarks" />
            </label>
          </div>
        )}
        {dest?.blocked_reason && <div className="text-xs text-red-300" data-testid="mt-v2-blocked">{dest.blocked_reason}</div>}
        {dest?.ack_required && (
          <div className="border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm rounded-sm p-3 space-y-2" data-testid="mt-v2-route-warning">
            <div className="flex items-center gap-2"><WarningIcon size={14} weight="bold" />{dest.warning}</div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} data-testid="mt-v2-ack" />
              I confirm transferring to {stage === "FINISHING" ? "Finishing" : "Dyeing"} despite the recorded WO route.
            </label>
          </div>
        )}
        {qty !== "" && row && (q <= 0 || q > row.available_qty) && <div className="text-xs text-red-300" data-testid="mt-v2-qty-error">Quantity must be &gt; 0 and ≤ available.</div>}
        <DialogFooter>
          <button onClick={onClose} className={`${btn} border border-[var(--border)] text-zinc-300`}>Cancel</button>
          <button onClick={post} disabled={!canPost} className={`${btn} bg-[var(--accent)] text-white`} data-testid="mt-v2-post">{busy ? "Posting…" : "Post Transfer"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReverseDialog({ row, includeTest, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(newKey());
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/material-transfers/v2/${row.transfer_pair_id}/reverse${includeTest ? "?include_test=true" : ""}`, { reason, idempotency_key: keyRef.current });
      toast.success(`Transfer ${row.transfer_no} reversed`); onDone(); onClose();
    } catch (e) {
      toast.error(errText(e));
      if (e?.response?.status === 409) { onDone(); onClose(); }
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mt-v2-reverse-dialog">
        <DialogHeader><DialogTitle>Reverse {row?.transfer_no}</DialogTitle></DialogHeader>
        <input className={inp} placeholder="Reason (required)" value={reason} onChange={e => setReason(e.target.value)} data-testid="mt-v2-reverse-reason" />
        <DialogFooter>
          <button onClick={onClose} className={`${btn} border border-[var(--border)] text-zinc-300`}>Cancel</button>
          <button onClick={go} disabled={busy || reason.replace(/[^A-Za-z0-9]/g, "").length < 4} className={`${btn} bg-red-600 text-white`} data-testid="mt-v2-reverse-confirm">Reverse</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MaterialTransfer() {
  const [reg, setReg] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [open, setOpen] = useState(false);
  const [rev, setRev] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const { ready, can, administrator } = useRbac();
  const [showTest, setShowTest] = useState(false);
  const [hiddenTests, setHiddenTests] = useState(0);
  const withTest = !!ready && !!administrator && showTest;
  const qs = withTest ? "include_test=true" : "";
  const canCreate = !!ready && can(MOD, "create");
  const canReverse = !!ready && can(MOD, "reverse");
  const canView = !!ready && can(MOD, "view");
  const [det, setDet] = useState(null);
  const [seq, setSeq] = useState(0);
  const panelRef = useRef(null);
  const openPid = useRef(null);
  const detail = (det?.transfer?.transfer_pair_id === detailId && det.transfer) || reg.find(r => r.transfer_pair_id === detailId) || null;
  const fetchDetail = (pid) => api.get(`/material-transfers/v2/${pid}${qs ? `?${qs}` : ""}`)
    .then(r => setDet(cur => (cur?.pid === pid ? { ...r.data, pid } : cur)))
    .catch(e => { const m = errText(e); toast.error(m); setDet(cur => (cur?.pid === pid ? { pid, legs: [], error: m } : cur)); });
  const openDetail = (pid) => { openPid.current = pid; setDetailId(pid); setDet({ pid, legs: null }); setSeq(n => n + 1); fetchDetail(pid); };
  const closeDetail = () => { openPid.current = null; setDetailId(null); setDet(null); };
  useEffect(() => { if (seq) panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }); }, [seq]);
  const reversible = (r) => r.can_reverse && (!r.is_test || withTest);
  const revRow = rev && reg.find(r => r.transfer_pair_id === rev.transfer_pair_id && reversible(r));

  const load = async () => {
    try { const r = await api.get(`/material-transfers/v2/register?limit=200${qs ? `&${qs}` : ""}`); setReg(r.data?.rows || []); setHiddenTests(r.data?.test_records_hidden || 0); }
    catch (e) { toast.error(errText(e)); }
    if (openPid.current) fetchDetail(openPid.current);
    api.get("/material-transfers/").then(r => setLegacy(r.data || [])).catch(() => setLegacy([]));
  };
  // Reload on mount and when test-record visibility changes; load() otherwise reads only refs, setters and api.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [withTest]);

  return (
    <div className="p-6 space-y-5" data-testid="material-transfer-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><TruckIcon size={11} weight="bold" />Production &amp; Planning · Material Transfer</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Material Transfer</h1>
          <div className="text-xs text-zinc-500 mt-1">Weaving WIP → Finishing / Dyeing WIP, posted on the V2 stock ledger.</div>
        </div>
        {canCreate && <button data-testid="mt-new-btn" onClick={() => setOpen(true)} className={`${btn} bg-[var(--accent)] hover:bg-blue-600 text-white flex items-center gap-2`}>
          <ArrowRightIcon size={14} weight="bold" />New Transfer</button>}
      </div>
      {ready && administrator && <label className="flex items-center gap-2 text-xs text-zinc-400 w-fit cursor-pointer" data-testid="mt-v2-show-test-label">
        <input type="checkbox" data-testid="mt-v2-show-test" checked={showTest} onChange={e => setShowTest(e.target.checked)} />
        Show test records (Administrator){!withTest && hiddenTests ? <span className="text-zinc-500" data-testid="mt-v2-test-hidden-count">· {hiddenTests} hidden</span> : null}
      </label>}

      <div className="tactical-card overflow-x-auto" data-testid="mt-v2-register">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
            {["Transfer No", "Date", "From → To", "WO / CWO", "Product", "Batch", "Qty (kg)", "Route", "Status", "Actions"].map(h => <th key={h} className={`text-left px-3 py-3 font-medium${h === "Actions" ? " " + STICKY : ""}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {reg.map(r => (
              <tr key={r.transfer_pair_id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40 cursor-pointer" onClick={() => canView && openDetail(r.transfer_pair_id)} data-testid={`mt-v2-row-${r.transfer_pair_id}`}>
                <td className="px-3 py-3 font-mono text-white">{r.transfer_no}</td>
                <td className="px-3 py-3 font-mono text-zinc-400">{String(r.date || "").slice(0, 10)}</td>
                <td className="px-3 py-3 text-zinc-300">{r.from_godown_code} <ArrowRightIcon className="inline mx-1 text-[var(--accent)]" size={12} weight="bold" /> {r.to_godown_code}</td>
                <td className="px-3 py-3"><WoRef r={r} /></td>
                <td className="px-3 py-3 font-mono text-zinc-400">{r.product_code}</td>
                <td className="px-3 py-3 font-mono text-zinc-400">{r.batch_lot}</td>
                <td className="px-3 py-3 font-mono text-white">{r.qty}</td>
                <td className="px-3 py-3 text-xs text-zinc-400">{r.route ? `${r.route.recorded_stage} → ${r.route.selected_stage}${r.route.ack_required ? " (ack)" : ""}` : "—"}</td>
                <td className="px-3 py-3"><StatusBadge s={r.status} id={r.transfer_pair_id} /></td>
                <td className={`px-3 py-3 text-right ${STICKY}`} data-testid={`mt-v2-actions-${r.transfer_pair_id}`}>
                  <div className="inline-flex items-center gap-1 whitespace-nowrap">
                  {canView && <button data-testid={`mt-v2-view-${r.transfer_pair_id}`} onClick={e => { e.stopPropagation(); openDetail(r.transfer_pair_id); }}
                    aria-label={`View transfer ${r.transfer_no}`} title="View" className="p-1.5 rounded-sm border border-[var(--border)] text-[var(--accent)] hover:bg-[var(--bg-2)]"><EyeIcon size={14} /></button>}
                  {canReverse && reversible(r) && <button data-testid={`mt-v2-reverse-${r.transfer_pair_id}`} onClick={e => { e.stopPropagation(); setRev(r); }}
                    className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400" title="Reverse" aria-label={`Reverse transfer ${r.transfer_no}`}><ArrowCounterClockwiseIcon size={14} /></button>}
                  </div>
                  {canReverse && !r.can_reverse && r.status === "Active" && r.reverse_block_reason && <div data-testid={`mt-v2-reverse-blocked-${r.transfer_pair_id}`}
                    className="mt-1 text-[11px] leading-snug text-zinc-500 whitespace-nowrap cursor-help underline decoration-dotted underline-offset-2"
                    title={r.reverse_block_reason} aria-label={`Not reversible: ${r.reverse_block_reason}`}>Not reversible</div>}
                </td>
              </tr>
            ))}
            {reg.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-zinc-500">No material transfers yet</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && canView && <DetailPanel d={detail} legs={det?.pid === detailId ? det.legs ?? null : null} legsError={det?.error} panelRef={panelRef}
        onClose={closeDetail} />}

      <div className="tactical-card overflow-x-auto" data-testid="mt-legacy-log">
        <div className="px-4 py-2 text-xs uppercase tracking-widest text-zinc-500">Historical metres log (read-only, no stock posting)</div>
        <table className="w-full text-sm"><tbody>
          {legacy.map(r => <tr key={r.id} className="border-t border-[var(--border)]" data-testid={`mt-legacy-row-${r.id}`}>
            <td className="px-4 py-2 font-mono text-zinc-400">{r.transfer_date}</td><td className="px-4 py-2 text-zinc-300">{r.from_stage} → {r.to_stage}</td>
            <td className="px-4 py-2 font-mono text-zinc-400">{r.qty_meters} m</td><td className="px-4 py-2 text-zinc-500">{r.remarks}</td></tr>)}
          {legacy.length === 0 && <tr><td className="px-4 py-3 text-zinc-500">No historical log entries</td></tr>}
        </tbody></table>
      </div>

      <NewTransferDialog open={open && canCreate} onClose={() => setOpen(false)} onPosted={load} />
      <ReverseDialog row={canReverse ? revRow || null : null} includeTest={!!revRow?.is_test && withTest} onClose={() => setRev(null)} onDone={load} />
    </div>
  );
}
